<script setup lang="ts">
import { Cookie, X } from "lucide-vue-next";

const localeRoute = useLocaleRoute();
const {
  analyticsAccepted,
  bannerOpen,
  consent,
  preferencesOpen,
  acceptAllCookies,
  closeCookiePreferences,
  initialiseCookieConsent,
  openCookiePreferences,
  rejectOptionalCookies,
  saveCookiePreferences,
} = useCookieConsent();

const analyticsEnabled = ref(false);
let previousBodyOverflow = "";

watch(
  preferencesOpen,
  isOpen => {
    if (isOpen) {
      analyticsEnabled.value = analyticsAccepted.value;
    }
  },
  { immediate: true },
);

watch(
  () => consent.value?.analytics,
  value => {
    if (!preferencesOpen.value) {
      analyticsEnabled.value = value === true;
    }
  },
);

watch(preferencesOpen, isOpen => {
  if (!import.meta.client) return;

  if (isOpen) {
    previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return;
  }

  document.body.style.overflow = previousBodyOverflow;
});

function handleKeydown(event: KeyboardEvent) {
  if (event.key === "Escape" && preferencesOpen.value) {
    closeCookiePreferences();
  }
}

onMounted(() => {
  initialiseCookieConsent();
  window.addEventListener("keydown", handleKeydown);
});

onBeforeUnmount(() => {
  window.removeEventListener("keydown", handleKeydown);
  document.body.style.overflow = previousBodyOverflow;
});

function savePreferences() {
  saveCookiePreferences(analyticsEnabled.value);
}
</script>

<template>
  <Teleport to="body">
    <Transition name="cookie-overlay">
      <div
        v-if="bannerOpen"
        class="fixed inset-0 z-[2100] bg-black/40"
        aria-hidden="true"
      />
    </Transition>

    <Transition name="cookie-banner">
      <div
        v-if="bannerOpen"
        class="fixed inset-x-3 bottom-3 z-[2101] mx-auto max-w-[760px] rounded-[22px] border border-app-border bg-white p-5 shadow-[0_24px_80px_rgba(25,31,28,0.24)] sm:inset-x-6 sm:bottom-6 sm:p-6"
        role="dialog"
        aria-modal="true"
        :aria-label="$t('cookie_consent.banner.aria_label')"
      >
        <div class="flex items-start justify-between gap-3 sm:gap-5">
          <div class="flex min-w-0 items-start gap-3.5">
            <div
              class="mt-0.5 flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary"
            >
              <Cookie class="size-5" />
            </div>

            <div class="min-w-0 max-w-[570px]">
              <h2 class="font-gabarito text-2xl font-bold text-app-text">
                {{ $t("cookie_consent.banner.title") }}
              </h2>

              <p class="mt-2 text-sm leading-6 text-app-muted sm:text-[15px]">
                {{ $t("cookie_consent.banner.description") }}
              </p>

              <NuxtLink
                :to="localeRoute('/cookies') || '/cookies'"
                class="mt-2 inline-flex text-sm font-semibold text-primary animated-underline"
              >
                {{ $t("cookie_consent.banner.policy_link") }}
              </NuxtLink>
            </div>
          </div>

          <div class="w-[82px] shrink-0 sm:w-[88px]">
            <LangSelect />
          </div>
        </div>

        <div class="mt-5 grid gap-2.5 sm:grid-cols-3">
          <button
            type="button"
            class="min-h-11 rounded-xl border border-primary bg-white px-4 py-2.5 text-sm font-bold text-primary transition hover:bg-primary/[0.06] focus:outline-none focus:ring-2 focus:ring-primary/25"
            @click="rejectOptionalCookies"
          >
            {{ $t("cookie_consent.actions.reject_optional") }}
          </button>

          <button
            type="button"
            class="min-h-11 rounded-xl border border-app-border bg-white px-4 py-2.5 text-sm font-bold text-app-text transition hover:border-primary/50 hover:bg-primary/[0.04] focus:outline-none focus:ring-2 focus:ring-primary/25"
            @click="openCookiePreferences"
          >
            {{ $t("cookie_consent.actions.settings") }}
          </button>

          <button
            type="button"
            class="min-h-11 rounded-xl border border-primary bg-white px-4 py-2.5 text-sm font-bold text-primary transition hover:bg-primary/[0.06] focus:outline-none focus:ring-2 focus:ring-primary/25"
            @click="acceptAllCookies"
          >
            {{ $t("cookie_consent.actions.accept_all") }}
          </button>
        </div>
      </div>
    </Transition>

    <Transition name="cookie-modal">
      <div
        v-if="preferencesOpen"
        class="fixed inset-0 z-[2102] flex items-end justify-center bg-black/45 p-3 sm:items-center sm:p-6"
        role="dialog"
        aria-modal="true"
        :aria-label="$t('cookie_consent.preferences.aria_label')"
        @click.self="closeCookiePreferences"
      >
        <div
          class="max-h-[calc(100dvh-24px)] w-full max-w-[620px] overflow-y-auto rounded-[22px] border border-app-border bg-white shadow-[0_30px_100px_rgba(25,31,28,0.24)]"
        >
          <div
            class="flex items-start justify-between gap-5 border-b border-app-border px-5 py-5 sm:px-6"
          >
            <div class="flex items-start gap-3.5">
              <div
                class="mt-0.5 flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary"
              >
                <Cookie class="size-5" />
              </div>

              <div>
                <h2 class="font-gabarito text-2xl font-bold text-app-text">
                  {{ $t("cookie_consent.preferences.title") }}
                </h2>
                <p class="mt-2 text-sm leading-6 text-app-muted">
                  {{ $t("cookie_consent.preferences.description") }}
                </p>
              </div>
            </div>

            <button
              type="button"
              class="flex size-10 shrink-0 items-center justify-center rounded-full text-app-muted transition hover:bg-gray-50 hover:text-app-text focus:outline-none focus:ring-2 focus:ring-primary/25"
              :aria-label="$t('cookie_consent.actions.close')"
              @click="closeCookiePreferences"
            >
              <X class="size-5" />
            </button>
          </div>

          <div class="space-y-3 px-5 py-5 sm:px-6">
            <div class="rounded-2xl border border-app-border p-4 sm:p-5">
              <div class="flex items-start justify-between gap-5">
                <div>
                  <div class="flex flex-wrap items-center gap-2">
                    <h3 class="font-bold text-app-text">
                      {{ $t("cookie_consent.preferences.necessary.title") }}
                    </h3>
                    <span
                      class="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-bold text-primary"
                    >
                      {{
                        $t("cookie_consent.preferences.necessary.always_active")
                      }}
                    </span>
                  </div>
                  <p class="mt-2 text-sm leading-6 text-app-muted">
                    {{ $t("cookie_consent.preferences.necessary.description") }}
                  </p>
                </div>

                <div
                  class="relative mt-0.5 h-6 w-11 shrink-0 rounded-full bg-primary"
                  aria-hidden="true"
                >
                  <span
                    class="absolute right-0.5 top-0.5 size-5 rounded-full bg-white shadow-sm"
                  ></span>
                </div>
              </div>
            </div>

            <div class="rounded-2xl border border-app-border p-4 sm:p-5">
              <div class="flex items-start justify-between gap-5">
                <label class="min-w-0 flex-1" for="cookie-analytics-toggle">
                  <h3 class="font-bold text-app-text">
                    {{ $t("cookie_consent.preferences.analytics.title") }}
                  </h3>
                  <p class="mt-2 text-sm leading-6 text-app-muted">
                    {{ $t("cookie_consent.preferences.analytics.description") }}
                  </p>
                </label>

                <button
                  id="cookie-analytics-toggle"
                  type="button"
                  role="switch"
                  :aria-checked="analyticsEnabled"
                  class="relative mt-0.5 h-6 w-11 shrink-0 rounded-full transition focus:outline-none focus:ring-2 focus:ring-primary/25 focus:ring-offset-2"
                  :class="analyticsEnabled ? 'bg-primary' : 'bg-gray-300'"
                  @click="analyticsEnabled = !analyticsEnabled"
                >
                  <span
                    class="absolute top-0.5 size-5 rounded-full bg-white shadow-sm transition"
                    :class="analyticsEnabled ? 'left-[22px]' : 'left-0.5'"
                  ></span>
                </button>
              </div>
            </div>

            <NuxtLink
              :to="localeRoute('/cookies') || '/cookies'"
              class="inline-flex pt-1 text-sm font-semibold text-primary animated-underline"
              @click="closeCookiePreferences"
            >
              {{ $t("cookie_consent.preferences.policy_link") }}
            </NuxtLink>
          </div>

          <div
            class="grid gap-2.5 border-t border-app-border px-5 py-5 sm:grid-cols-3 sm:px-6"
          >
            <button
              type="button"
              class="min-h-11 rounded-xl border border-app-border bg-white px-4 py-2.5 text-sm font-bold text-app-text transition hover:border-primary/50 hover:bg-primary/[0.04] focus:outline-none focus:ring-2 focus:ring-primary/25"
              @click="rejectOptionalCookies"
            >
              {{ $t("cookie_consent.actions.reject_optional") }}
            </button>

            <button
              type="button"
              class="min-h-11 rounded-xl border border-app-border bg-white px-4 py-2.5 text-sm font-bold text-app-text transition hover:border-primary/50 hover:bg-primary/[0.04] focus:outline-none focus:ring-2 focus:ring-primary/25"
              @click="savePreferences"
            >
              {{ $t("cookie_consent.actions.save") }}
            </button>

            <button
              type="button"
              class="min-h-11 rounded-xl border border-primary bg-white px-4 py-2.5 text-sm font-bold text-primary transition hover:bg-primary/[0.06] focus:outline-none focus:ring-2 focus:ring-primary/25"
              @click="acceptAllCookies"
            >
              {{ $t("cookie_consent.actions.accept_all") }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.cookie-overlay-enter-active,
.cookie-overlay-leave-active,
.cookie-banner-enter-active,
.cookie-banner-leave-active,
.cookie-modal-enter-active,
.cookie-modal-leave-active {
  transition:
    opacity 160ms ease,
    transform 160ms ease;
}

.cookie-overlay-enter-from,
.cookie-overlay-leave-to,
.cookie-modal-enter-from,
.cookie-modal-leave-to {
  opacity: 0;
}

.cookie-banner-enter-from,
.cookie-banner-leave-to {
  opacity: 0;
  transform: translateY(12px);
}

@media (prefers-reduced-motion: reduce) {
  .cookie-overlay-enter-active,
  .cookie-overlay-leave-active,
  .cookie-banner-enter-active,
  .cookie-banner-leave-active,
  .cookie-modal-enter-active,
  .cookie-modal-leave-active {
    transition: none;
  }
}
</style>
